import type { ApolloQueryResult } from "@apollo/client";
import { useMemo } from "react";
import type { AppointmentListFragment } from "@/gql/graphql";
import { StandardError } from "@/lib/error/error-utils";
import { useAppointmentsQuery } from "./use-appointments-query";

interface UseAppointmentsProps {
  currentDate: Date;
  viewType: "day" | "3-day" | "week" | "month" | "worker";
  salonId?: string;
}

interface UseAppointmentsReturn {
  appointments: AppointmentListFragment[];
  loading: boolean;
  error: StandardError | null;
  retry: () => void;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
}

/**
 * Main hook for appointments with user-friendly error handling and data transformation
 */
export const useAppointments = ({
  currentDate,
  viewType,
  salonId,
}: UseAppointmentsProps): UseAppointmentsReturn => {
  // Use the query hook with standardized error handling
  const { data, loading, error, retry, refetch } = useAppointmentsQuery({
    currentDate,
    viewType,
    salonId,
  });

  // Apply any business logic transformations here
  const appointments = useMemo(() => {
    // For now, return data as-is, but this is where you could:
    // - Filter based on business rules
    // - Sort by custom logic
    // - Add computed fields
    return data;
  }, [data]);

  return {
    appointments,
    loading,
    error,
    retry,
    refetch,
  };
};
